'use client';

import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { COLOR_BACKGROUND_TERTIARY } from '@alltrails/denali/tokens';
import type { TrailId } from '@alltrails/types/TrailId';
import useDeeplink from '../../hooks/useDeeplink';
import logo from './logo.svg';

type AppsFlyerQrCodeProps = {
  trailId: TrailId;
  size?: number;
  logoBackgroundColor?: string;
};

const AppsFlyerQrCode = ({ trailId, size = 64, logoBackgroundColor = COLOR_BACKGROUND_TERTIARY }: AppsFlyerQrCodeProps) => {
  const intl = useIntl();
  const getDeeplink = useDeeplink({ deeplinkData: { pageId: trailId, pageType: 'trail' } });
  const qrCodeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const clickUrl = getDeeplink();
    const container = qrCodeRef.current;

    // displayQrCode copied from onelink-smart-script-latest.js so we can render the QR
    // code at its target size natively instead of CSS-scaling a 256px canvas down to
    // 64px. The logo sits over the center, so we keep the Q correction level (like the
    // script does) to stay scannable despite the modules the logo covers.
    if (window.QRCode && container) {
      new window.QRCode(container, {
        colorDark: '#000',
        colorLight: 'rgba(0,0,0,0)',
        correctLevel: window.QRCode.CorrectLevel.Q,
        crossOrigin: 'anonymous',
        height: size, // we default to 64 but usually ends up around 77px and it can't be scaled down or the phone can't scan it
        logo: logo.src,
        logoBackgroundColor,
        logoBackgroundTransparent: false,
        text: `${clickUrl}&af_ss_qr=c `, // this space is intentional from the code in onelink-smart-script-latest.js
        width: size // we default to 64 but usually ends up around 77px and it can't be scaled down or the phone can't scan it
      });
    }

    return () => {
      container?.replaceChildren();
    };
  }, [getDeeplink]);

  return (
    <div
      ref={qrCodeRef}
      role="img"
      aria-label={intl.formatMessage({
        defaultMessage:
          'This QR code is intended to scan from a smartphone. If you have the AllTrails app, the link will open the app for you. If you don’t have the app, the link will take you to your device’s app store.'
      })}
    />
  );
};

export default AppsFlyerQrCode;
